import type { PermissionAction, PermissionEffect, PrincipalType } from '@fleet/shared';
import { apiFetch } from './client';

export interface PermissionEntry {
  action: PermissionAction;
  effect: PermissionEffect;
  principalType: PrincipalType;
  entraObjectId: string;
  displayName: string;
  /**
   * Ob der Anzeigename in diesem Aufruf frisch aus dem Verzeichnis kam.
   *
   * `false` heißt: Der Name stammt aus der gespeicherten Kopie — entweder weil
   * Graph gerade nicht antwortet oder weil das Objekt im Verzeichnis nicht mehr
   * existiert. Es ist **keine** Aussage darüber, ob die Berechtigung gilt.
   */
  resolved: boolean;
}

export interface DirectoryPrincipal {
  principalType: PrincipalType;
  entraObjectId: string;
  displayName: string;
  secondary?: string;
}

export const permissionsApi = {
  /** Liefert BOOK- und APPROVE-Einträge gemischt in einer flachen Liste. */
  list: (vehicleGroupId: string) =>
    apiFetch<PermissionEntry[]>(`/api/v1/vehicle-groups/${vehicleGroupId}/permissions`),

  /**
   * Ersetzt **alle** Einträge der Fahrzeuggruppe — über beide Aktionen hinweg.
   *
   * Der Endpunkt löscht ohne Filter auf `action`. Wer nur die Buchungsrechte
   * sendet, löscht damit sämtliche Freigaberechte still mit. Der Editor hält
   * deshalb beide Listen im Entwurf und schreibt sie immer gemeinsam zurück.
   */
  replace: (vehicleGroupId: string, permissions: Omit<PermissionEntry, 'resolved'>[]) =>
    apiFetch<{ count: number }>(`/api/v1/vehicle-groups/${vehicleGroupId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    }),
};

export const directoryApi = {
  /**
   * Sucht Personen und Sicherheitsgruppen im Verzeichnis.
   *
   * Ab zwei Zeichen; darunter antwortet die API mit einer leeren Liste. Eine
   * Störung kommt dagegen als `DIRECTORY_UNAVAILABLE` zurück und ist damit von
   * „keine Treffer" unterscheidbar.
   */
  search: (query: string, type?: PrincipalType) => {
    const params = new URLSearchParams({ q: query });
    if (type) params.set('type', type);
    return apiFetch<DirectoryPrincipal[]>(`/api/v1/directory/search?${params}`);
  },
};
